import { Add, Close, Delete, Save } from '@mui/icons-material';
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    List,
    ListItem,
    Paper,
    Stack,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { StatusOption } from '@sharedInterfaces/ICompany';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import updateOpportunityStatusOptions from '@src/APIs/graphQl/Company/updateOpportunityStatusOptions';
import { setCompanyOpportunitySettings } from '@store/reducer/companyReducer';
import type { AppState } from '@store/store';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

interface MvpOpportunitySettingsDialogProps {
    open: boolean;
    onClose: () => void;
}

/**
 * Modern MVP Opportunity Settings Dialog
 * Manages status options for opportunities with theme-aware design
 */
function MvpOpportunitySettingsDialog({ open, onClose }: MvpOpportunitySettingsDialogProps) {
    const dispatch = useDispatch();
    const theme = useTheme();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).opportunities;
    const statusOptionsFromStore = useSelector((state: AppState) => state.company.opportunitySettings.statusOptions);

    const [statusOptions, setStatusOptions] = useState<StatusOption[]>([]);
    const [newStatusName, setNewStatusName] = useState<string>('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string>('');

    useEffect(() => {
        setStatusOptions(statusOptionsFromStore);
        setNewStatusName('');
    }, [statusOptionsFromStore]);

    const handleAddStatus = () => {
        if (newStatusName.trim() === '') return;
        const newStatus: StatusOption = {
            id: Date.now().toString(),
            title: newStatusName.trim(),
            isActive: true,
        };
        setStatusOptions([...statusOptions, newStatus]);
        setNewStatusName('');
    };

    const handleDeleteStatus = (id: string) => {
        setStatusOptions(statusOptions.filter((status) => status.id !== id));
    };

    const handleNameChange = (id: string, newName: string) => {
        setStatusOptions(statusOptions.map((status) => (status.id === id ? { ...status, title: newName } : status)));
    };

    const handleToggleActive = (id: string) => {
        setStatusOptions(
            statusOptions.map((status) => (status.id === id ? { ...status, isActive: !status.isActive } : status)),
        );
    };

    const handleSave = async () => {
        setSaving(true);
        setError('');
        try {
            await updateOpportunityStatusOptions(statusOptions);
            dispatch(setCompanyOpportunitySettings({ statusOptions }));
            onClose();
        } catch (ex) {
            setError(ex?.toString() || 'Fehler beim Speichern');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    background: theme.palette.background.paper,
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pb: 2,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                }}
            >
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        {langStrings.settings}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Status-Optionen verwalten
                    </Typography>
                </Box>
                <IconButton onClick={onClose} sx={{ color: 'text.secondary' }}>
                    <Close />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>
                {statusOptions.length > 0 && (
                    <List sx={{ mb: 3 }}>
                        {statusOptions.map((status, index) => (
                            <React.Fragment key={status.id}>
                                <ListItem
                                    sx={{
                                        px: 0,
                                        py: 2,
                                        '&:hover': {
                                            bgcolor: alpha(theme.palette.primary.main, 0.02),
                                        },
                                    }}
                                >
                                    <Stack direction="row" spacing={2} sx={{ width: '100%', alignItems: 'center' }}>
                                        <TextField
                                            value={status.title}
                                            onChange={(e) => handleNameChange(status.id, e.target.value)}
                                            label={langStrings.statusName}
                                            variant="outlined"
                                            size="small"
                                            sx={{ flex: 1 }}
                                        />

                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Typography variant="body2" color="text.secondary">
                                                {langStrings.active}
                                            </Typography>
                                            <Checkbox
                                                checked={status.isActive}
                                                onChange={() => handleToggleActive(status.id)}
                                                color="primary"
                                            />
                                        </Stack>

                                        <IconButton
                                            onClick={() => handleDeleteStatus(status.id)}
                                            sx={{
                                                color: 'error.main',
                                                '&:hover': {
                                                    bgcolor: alpha(theme.palette.error.main, 0.1),
                                                },
                                            }}
                                        >
                                            <Delete />
                                        </IconButton>
                                    </Stack>
                                </ListItem>
                                {index < statusOptions.length - 1 && <Divider />}
                            </React.Fragment>
                        ))}
                    </List>
                )}

                <Paper
                    elevation={0}
                    sx={{
                        p: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.03),
                        border: `1px solid ${theme.palette.divider}`,
                    }}
                >
                    <Stack direction="row" spacing={2} alignItems="center">
                        <TextField
                            value={newStatusName}
                            onChange={(e) => setNewStatusName(e.target.value)}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    handleAddStatus();
                                }
                            }}
                            label={langStrings.newStatus}
                            variant="outlined"
                            size="small"
                            fullWidth
                            placeholder="Neuen Status eingeben..."
                        />
                        <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={handleAddStatus}
                            disabled={!newStatusName.trim()}
                            sx={{
                                minWidth: 120,
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {langStrings.oftenUsed.add}
                        </Button>
                    </Stack>
                </Paper>

                {error && (
                    <Box
                        sx={{
                            mt: 2,
                            p: 2,
                            bgcolor: alpha(theme.palette.error.main, 0.1),
                            border: `1px solid ${theme.palette.error.main}`,
                            borderRadius: 1,
                            color: 'error.main',
                        }}
                    >
                        <Typography variant="body2">{error}</Typography>
                    </Box>
                )}
            </DialogContent>

            <DialogActions
                sx={{
                    p: 3,
                    borderTop: `1px solid ${theme.palette.divider}`,
                    gap: 2,
                }}
            >
                <Button onClick={onClose} variant="outlined" color="secondary" sx={{ minWidth: 120 }}>
                    {langStrings.oftenUsed.cancel}
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    startIcon={<Save />}
                    disabled={saving}
                    sx={{
                        minWidth: 120,
                        background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.dark} 90%)`,
                        '&:hover': {
                            background: `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.primary.main} 90%)`,
                        },
                    }}
                >
                    {saving ? 'Speichern...' : langStrings.save}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default MvpOpportunitySettingsDialog;
